import React, { useState, useEffect } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { CancelTokenSource } from 'axios';
import { get } from 'lodash';
import { RootState } from 'modules';
import scrollIntoView from 'scroll-into-view';
import OrderingButton from 'components/base/OrderingButton';
import ResettableOrderingButton from 'components/base/ResettableOrderingButton';
import ResettableSearchInput from 'components/base/ResettableSearchInput';
import SearchInput from 'components/base/SearchInput';
import LoginRequiredModal from 'components/community/parts/modal/LoginRequiredModal';
import QuizListSearchInput from 'components/learning/quiz/components/parts/QuizListSearchInput';
import apiClient from 'libs/api/apiClient';
import { getInterviewReviewList, communityHitConut } from 'libs/api/community';
import { usePagination, useDataApi } from 'libs/hooks';
import { makeLastNYearsList } from 'libs/utils';
import { TListResponse, TPagination } from 'modules/types';
import { TModalState } from 'modules/types';
import { CWindowWidth } from 'constants/base.const';
import { CEmploymentType } from 'constants/community.const';
import { EEmploymentType } from 'enums/community.enum';
import { ERoute } from 'enums/route.enum';
import { Button, Select, List, Form, Typography } from 'antd';
import { RightOutlined, SearchOutlined, CloseOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { device, PrimaryColor } from 'GlobalStyles';
import refresh from 'assets/icons/refresh.svg';
import topBanner from 'assets/images/banners/coffee.webp';
import defaultCompanyImg from 'assets/images/pages/community/defaultCompany.webp';
import defaultUnivImg from 'assets/images/pages/community/defaultUniv.webp';
import enrollTicket from 'assets/images/pages/community/enrollTicket.webp';
import integratedCompanyImg from 'assets/images/pages/community/integratedCompany.webp';

const { Text, Title } = Typography;

type TResponsive = {
  isActive?: string;
};

const FinalAcceptanceList = () => {
  const [pagination, setPagination] = usePagination(10);
  const [isFirstRendering, setIsFirstRendering] = useState(true);
  const [searchBar, setSearchBar] = useState(false);
  const [searchInputText, setSearchInputText] = useState<string | undefined>(pagination.q);
  const [selectedOrderingBtn, setSelectedOrderingBtn] = useState('등록순');
  const [form] = Form.useForm();
  const history = useHistory();
  const currentUser = useSelector((state: RootState) => state.user.current_user, shallowEqual);
  const [loginRequestModal, setLoginRequestModal] = useState<TModalState>({ visible: false });

  const reloadPage = (page?: Partial<TPagination>) => {
    setPagination({
      ...pagination,
      ...page,
    });
    setIsFirstRendering(false);
  };

  const getFinalAcceptanceReview = ({ params }: any, cancel?: CancelTokenSource) => {
    return apiClient.get(`/community/final_reviews`, {
      params: params,
      cancelToken: cancel?.token,
    });
  };

  const getPromise = getFinalAcceptanceReview.bind(null, {
    params: {
      current: pagination.current,
      pageSize: pagination.pageSize,
      q: pagination.q,
      ordering: pagination.ordering ? pagination.ordering : 'created_at',
      employment_types: pagination.employment_types,
      interview_years: pagination.interview_years,
    },
  });

  const [{ data, loading }, setCallback] = useDataApi<TListResponse<any>>(getPromise, {
    contents: [],
    last: false,
  });

  useEffect(() => {
    setCallback(() => getPromise);
  }, [pagination]);

  const hitCount = async () => {
    const res = await communityHitConut({ board: 'final_interview_review' });
  };

  useEffect(() => {
    hitCount();
  }, []);

  function upToScroll(loading: boolean, isFirstRendering: boolean) {
    if (!loading && !isFirstRendering) {
      scrollIntoView(document?.getElementById('card-list-header') as HTMLElement, {
        align: {
          top: 0.3,
        },
      });
    }
  }

  const onClickRefresh = () => {
    reloadPage({
      employment_types: undefined,
      interview_years: undefined,
      current: 1,
      pageSize: pagination.pageSize,
      q: '',
      ordering: 'created_at',
    });
    form.setFieldsValue({
      employment_types: undefined,
      interview_years: undefined,
      is_consulting_firm: undefined,
    });
    setSearchInputText('');
    setSelectedOrderingBtn('등록순');
  };

  useEffect(() => {
    upToScroll(loading, isFirstRendering);
  }, [loading]);

  return (
    <StyledContainer>
      <StyledTopBanner>
        <Title className='pageTitle'>최종합격 후기</Title>
        <Text className='pageSubtitle'>
          최근 컨설팅펌 합격자들의 케이스 공부법과{<br />}그들만의 문제 풀이법을 확인하세요
        </Text>
      </StyledTopBanner>
      <Form form={form}>
        <section>
          <StyledGuideEnroll id='card-list-header'>
            <div className='guideHeader'>
              <img src={enrollTicket} alt='ticket' />
              <p>{'합격 후기를 등록하면 MBB 신입사원 대상으로 진행한 30만원 상당의 엑셀 강의를 드립니다'}</p>
            </div>

            <Button
              type='primary'
              onClick={() => {
                currentUser
                  ? history.push('/community/final_acceptance/write')
                  : setLoginRequestModal({ visible: true });
              }}
            >
              <div>합격 후기 등록하기</div>
            </Button>
            <LoginRequiredModal
              modalState={loginRequestModal}
              setModalState={setLoginRequestModal}
              nextPath={ERoute.FinalAcceptanceBoard}
            />
          </StyledGuideEnroll>

          <StyledReviews>
            <h1 className='title'>최종합격 후기</h1>

            <div className='filters'>
              <StyledSearchInput isActive='desktop'>
                <SearchInput
                  pagination={pagination}
                  reloadPage={reloadPage}
                  placeHolder='기업을 검색하세요'
                />
              </StyledSearchInput>
              <StyledSearchInput isActive='mobile'>
                <SearchOutlined onClick={() => setSearchBar(true)} />
                <ResettableSearchInput
                  pagination={pagination}
                  reloadPage={reloadPage}
                  searchInputText={searchInputText}
                  setSearchInputText={setSearchInputText}
                  suffix={<CloseOutlined onClick={() => setSearchBar(false)} />}
                  placeHolder='기업을 검색하세요'
                  style={{
                    display: searchBar ? 'flex' : 'none',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    minWidth: '100%',
                    height: '40px',
                    zIndex: 1,
                  }}
                />
              </StyledSearchInput>
              <div className='btns'>
                <Form.Item name='employment_types' noStyle>
                  <Select
                    style={{ width: '120px', color: PrimaryColor }}
                    className='employment-type-selector'
                    placeholder='고용형태'
                    onChange={(value) => {
                      reloadPage({
                        current: 1,
                        employment_types: String(value),
                      });
                    }}
                  >
                    <Select.Option value={EEmploymentType.Newcomer}>
                      {CEmploymentType[EEmploymentType.Newcomer]}
                    </Select.Option>
                    <Select.Option value={EEmploymentType.ExperiencedWorker}>
                      {CEmploymentType[EEmploymentType.ExperiencedWorker]}
                    </Select.Option>
                    <Select.Option value={EEmploymentType.TransitionalTypeIntern}>
                      {CEmploymentType[EEmploymentType.TransitionalTypeIntern]}
                    </Select.Option>
                    <Select.Option value={EEmploymentType.GeneralTypeIntern}>
                      {CEmploymentType[EEmploymentType.GeneralTypeIntern]}
                    </Select.Option>
                  </Select>
                </Form.Item>

                {/* <Form.Item name='interview_years' noStyle>
                  <Select
                    style={{ marginLeft: '6px', width: '100px', color: PrimaryColor }}
                    className='years-selector'
                    placeholder='연도'
                    onChange={(value) => {
                      reloadPage({
                        current: 1,
                        interview_years: Number(value),
                      });
                    }}
                  >
                    {makeLastNYearsList(4).map((year) => (
                      <Select.Option key={year} value={year}>
                        {year}년
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item> */}

                <ResettableOrderingButton
                  pagination={pagination}
                  reloadPage={reloadPage}
                  defaultKey='created_at'
                  orderingList={{
                    created_at: '등록순',
                    '-interview_info__interview_date': '면접일자순',
                  }}
                  selected={selectedOrderingBtn}
                  setSelected={setSelectedOrderingBtn}
                />
                <img className='refreshIcon' src={refresh} alt='refresh' onClick={onClickRefresh} />
              </div>
            </div>

            <div>
              <List
                pagination={{
                  ...pagination,
                  total: get(data, 'total', 0),
                  position: 'bottom',
                  showSizeChanger: false,
                  onChange: (page) => {
                    reloadPage({
                      current: page,
                      pageSize: pagination.pageSize,
                    });
                  },
                }}
                className='card'
                loading={loading}
                grid={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 1, xxl: 1 }}
                dataSource={get(data, 'contents')}
                renderItem={(item) => (
                  <List.Item>
                    <Link to={`/community/final_acceptance/${item.id}`}>
                      <StyledReview>
                        <div className='infoLeft'>
                          <div
                            style={{
                              marginRight: '10px',
                              width: '65px',
                              height: '65px',
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            <img
                              src={
                                item.company.company_name === 'McKinsey' ||
                                item.company.company_name === 'BCG' ||
                                item.company.company_name === 'Bain & Company' ||
                                item.company.company_name === 'McKinsey-Bain-Company' ||
                                item.company.company_name === 'McKinsey\u00b7BCG\u00b7Bain'
                                  ? integratedCompanyImg
                                  : item.company.company_logo_url
                                  ? item.company.company_logo_url
                                  : defaultCompanyImg
                              }
                              alt=''
                              style={{ width: '100%' }}
                            />
                          </div>
                          <div>
                            <h2 className='reviewTitle'>
                              {item.company.company_name === 'McKinsey' ||
                              item.company.company_name === 'BCG' ||
                              item.company.company_name === 'Bain & Company' ? (
                                <span>McKinsey&middot;BCG&middot;Bain</span>
                              ) : (
                                item.company.company_name
                              )}
                            </h2>
                            <div className='subtitles'>
                              <h3 className='employmentType'>
                                {item.employment_type === 'newcomer'
                                  ? '신입'
                                  : item.employment_type === 'experienced_worker'
                                  ? '경력'
                                  : item.employment_type === 'general_type_intern'
                                  ? '인턴'
                                  : '전환형 인턴'}
                              </h3>
                              <h3 className='employmentDate'>{`${item.interview_date.split('-')[0]}년 ${
                                item.created_at.split('-')[1]
                              }월`}</h3>
                            </div>
                          </div>
                        </div>

                        <div className='infoAndBtn'>
                          <div className='infoRight'>
                            <div
                              style={{
                                marginRight: '10px',
                                width: '58px',
                                height: '58px',
                                display: 'flex',
                                alignItems: 'center',
                              }}
                            >
                              <img
                                src={
                                  item.education.university_logo_url
                                    ? item.education.university_logo_url
                                    : defaultUnivImg
                                }
                                alt=''
                                style={{ width: '100%' }}
                              />
                            </div>
                            <div className='info'>
                              <div className='label'>학교명</div>
                              <div className='content'>
                                {item.education.university_name.indexOf('University') != -1
                                  ? (item.education.university_name = item.education.university_name.replace(
                                      /University/gi,
                                      'Univ.',
                                    ))
                                  : item.education.university_name}
                              </div>
                            </div>
                            <div className='info'>
                              <div className='label'>전공</div>
                              <div className='content'>{item.major}</div>
                            </div>
                          </div>
                          <RightOutlined />
                        </div>
                      </StyledReview>
                    </Link>
                  </List.Item>
                )}
              />
            </div>
          </StyledReviews>
        </section>
      </Form>
    </StyledContainer>
  );
};

export default FinalAcceptanceList;

const maxWidth = CWindowWidth - 450;

const StyledContainer = styled.main`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  word-break: keep-all;

  section {
    width: 100%;
    padding: 0 50px;

    @media screen and ${device.lg} {
      padding: 0 20px;
    }
  }
`;

export const StyledTopBanner = styled.div`
  width: 100%;
  height: 400px;
  padding: 0 10px;
  background-image: url(${topBanner});
  background-size: cover;

  @media screen and ${device.md} {
    height: 205px;
  }

  .pageTitle {
    margin: 150px auto 0 auto;
    font-size: 56px;
    font-weight: 700;

    @media screen and ${device.md} {
      margin-top: 60px;
      font-size: 30px;
    }
  }

  .pageSubtitle {
    margin: 16px auto 0 auto;
    font-size: 14px;

    @media screen and ${device.md} {
      font-size: 10px;
    }
  }

  .ant-typography {
    display: block;
    max-width: ${maxWidth}px;
    color: #ffffff;
  }
`;

export const StyledGuideEnroll = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 996px;
  width: 100%;
  margin: 30px auto 0 auto;
  padding: 40px 70px;
  border-radius: 8px;
  background-color: #f7f7f7;

  @media screen and ${device.lg} {
    flex-direction: column;
    padding: 10px;
  }

  .guideHeader {
    display: flex;
    align-items: center;
  }

  img {
    width: 134px;

    @media screen and ${device.md} {
      width: 80px;
    }
  }

  p {
    margin: 0 40px;
    font-size: 18px;
    white-space: pre-line;

    @media screen and ${device.lg} {
      margin: 0 15px;
      font-size: 14px;
    }

    @media screen and ${device.xl} {
      white-space: unset;
      text-align: initial;
    }
  }

  a {
    @media screen and ${device.lg} {
      display: flex;
      justify-content: center;
      width: 90%;
    }
  }

  .ant-btn.ant-btn-primary,
  .ant-btn.secondary {
    height: auto;
    padding: 12px 50px !important;

    @media screen and ${device.lg} {
      width: 90%;
      margin: 10px 0;
    }
  }
`;

export const StyledReviews = styled.ul`
  max-width: 996px;
  width: 100%;
  margin: 20px auto 0 auto;
  padding: 0;

  .title {
    margin-bottom: 20px;
    padding-bottom: 10px;
    border-bottom: 1px solid #e4e4e4;
    font-size: 24px;
    font-weight: 900;
    color: ${PrimaryColor};
  }

  .filters {
    display: flex;
    justify-content: space-between;
    align-items: center;

    @media screen and ${device.lg} {
      position: relative;
    }

    .refreshIcon {
      margin-left: 5px;
      padding: 7px;
      border-radius: 8px;
      background-color: #f9f9f9;
      cursor: pointer;
    }

    .ant-select-selection-placeholder {
      color: ${PrimaryColor};
    }

    .ant-dropdown-trigger {
      width: 130px;
    }
  }
`;

const StyledReview = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  padding: 13px 0 25px 25px;
  border-radius: 10px;
  list-style: none;
  box-shadow: 1px 1px 5px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    box-shadow: 2px 2px 8px rgba(0, 0, 0, 0.6);
  }

  @media screen and ${device.lg} {
    min-width: 50%;
    flex-direction: column;
    align-items: center;
    margin: 20px auto;
    padding: 15px;
  }

  .infoLeft {
    display: flex;
    align-items: center;
  }

  .reviewTitle {
    margin-bottom: 10px;
    font-size: 24px;
    font-weight: 700;

    @media screen and ${device.lg} {
      font-size: 20px;
    }
  }

  .subtitles {
    display: flex;
    align-items: center;

    h3 {
      margin: 0;
      font-size: 18px;
      line-height: 1;
      color: #14112a;

      @media screen and ${device.lg} {
        font-size: 14px;
      }
    }
  }

  .employmentType {
    padding-right: 10px;
    border-right: 1px solid #aeaeae;
  }

  .employmentDate {
    padding-left: 10px;
  }

  .infoRight {
    display: flex;
    align-items: center;
    width: 315px;
    padding: 15px;
    border-radius: 4px;
    background-color: #f9f9f9;

    @media screen and ${device.lg} {
      width: 100%;
    }
  }

  .info {
    width: 45%;

    @media screen and ${device.lg} {
      width: 88px;
    }
  }

  .label {
    margin-bottom: 3px;
    color: #aeaeae;

    @media screen and ${device.lg} {
      font-size: 14px;
    }
  }

  .content {
    display: -webkit-box;
    font-size: 18px;
    overflow: hidden;
    text-overflow: ellipsis;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    word-break: break-word;

    @media screen and ${device.lg} {
      font-size: 14px;
    }
  }

  .infoAndBtn {
    display: flex;
    align-items: center;

    @media screen and ${device.lg} {
      margin: 25px 0 0 20px;
    }

    .anticon.anticon-right {
      padding: 46px 28px;
      font-size: 24px;

      @media screen and ${device.lg} {
        padding: 35px 8px;
        font-size: 14px;
      }
    }
  }
`;

const StyledSearchInput = styled.div<TResponsive>`
  display: ${({ isActive }) => (isActive === 'desktop' ? 'block' : 'none')};
  /* width:  */

  @media screen and ${device.lg} {
    display: ${({ isActive }) => (isActive === 'mobile' ? 'block' : 'none')};

    .anticon.anticon-search {
      padding: 12px;
      border-radius: 7px;
      font-size: 16px;
      background-color: #f9f9f9;
    }
  }
`;
